<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps<{
  data: number[];
  labels: string[];
  color?: string;
  windowSize?: number;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const WINDOW_SIZE = props.windowSize ?? 10;

// Fenêtre glissante : offset = index de départ dans les données complètes
const offset = ref(0);

const maxOffset = computed(() => Math.max(0, props.data.length - WINDOW_SIZE));
const canPaginate = computed(() => props.data.length > WINDOW_SIZE);
const windowData = computed(() =>
  canPaginate.value
    ? props.data.slice(offset.value, offset.value + WINDOW_SIZE)
    : props.data,
);
const windowLabels = computed(() =>
  canPaginate.value
    ? props.labels.slice(offset.value, offset.value + WINDOW_SIZE)
    : props.labels,
);

function goPrev() {
  offset.value = Math.max(0, offset.value - WINDOW_SIZE);
}
function goNext() {
  offset.value = Math.min(maxOffset.value, offset.value + WINDOW_SIZE);
}

// Recale la fenêtre sur les données les plus récentes quand la série change
watch(
  () => props.data.length,
  () => {
    offset.value = maxOffset.value;
  },
  { immediate: true },
);

function drawChart() {
  const el = canvas.value;
  if (!el) return;
  const ctx = el.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const W = el.offsetWidth;
  const H = el.offsetHeight;
  el.width = W * dpr;
  el.height = H * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);

  const data = windowData.value;
  const n = data.length;
  if (!n) return;

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const textColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';
  const barColor = isDark ? '#e0e0e0' : '#111111';

  const pad = { top: 12, right: 8, bottom: 44, left: 52 };
  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top - pad.bottom;

  const maxVal = Math.max(...data) || 1;

  // Grid lines (4)
  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + (chartH / 4) * i;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(pad.left + chartW, y);
    ctx.stroke();
    const val = Math.round(maxVal - (maxVal / 4) * i);
    ctx.fillStyle = textColor;
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(
      val >= 1000 ? `${(val / 1000).toFixed(0)}k` : String(val),
      pad.left - 6,
      y + 4,
    );
  }

  const gap = Math.max(2, Math.round((chartW / n) * 0.18));
  const barW = (chartW - gap * (n - 1)) / n;

  for (let i = 0; i < n; i++) {
    const barH = (data[i] / maxVal) * chartH;
    const x = pad.left + i * (barW + gap);
    const y = pad.top + chartH - barH;

    // Bar
    ctx.fillStyle = barColor;
    ctx.beginPath();
    const r = Math.min(4, barW / 2);
    ctx.roundRect(x, y, barW, barH, [r, r, 0, 0]);
    ctx.fill();

    // Label
    ctx.fillStyle = textColor;
    ctx.font = `${Math.max(8, Math.min(11, Math.floor(barW * 0.8)))}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    const lbl = windowLabels.value[i] ?? '';
    // Truncate label if bar is narrow
    const maxChars = Math.max(3, Math.floor(barW / 5));
    const displayLbl =
      lbl.length > maxChars ? lbl.slice(0, maxChars - 1) + '…' : lbl;
    ctx.fillText(displayLbl, x + barW / 2, pad.top + chartH + 16);

    // Value above bar (if enough space)
    if (barH > 18) {
      ctx.fillStyle = isDark ? '#fff' : '#fff';
      ctx.font = `bold ${Math.max(8, Math.min(10, Math.floor(barW * 0.7)))}px system-ui, sans-serif`;
      const valStr =
        data[i] >= 1000 ? `${(data[i] / 1000).toFixed(0)}k` : String(data[i]);
      ctx.fillText(valStr, x + barW / 2, y + 12);
    }
  }
}

let ro: ResizeObserver | null = null;
onMounted(() => {
  drawChart();
  ro = new ResizeObserver(() => drawChart());
  if (canvas.value?.parentElement) ro.observe(canvas.value.parentElement);
});
onUnmounted(() => ro?.disconnect());
watch(() => [props.data, props.labels], drawChart, { deep: true });
watch(offset, drawChart);
</script>

<template>
  <div class="bar-wrap">
    <div v-if="canPaginate" class="bar-nav">
      <button
        class="bar-nav-btn"
        :disabled="offset === 0"
        @click="goPrev"
        aria-label="Précédent"
      >
        <i class="pi pi-chevron-left" />
      </button>
      <span class="bar-nav-label">
        {{ windowLabels[0] }} → {{ windowLabels[windowLabels.length - 1] }}
      </span>
      <button
        class="bar-nav-btn"
        :disabled="offset === maxOffset"
        @click="goNext"
        aria-label="Suivant"
      >
        <i class="pi pi-chevron-right" />
      </button>
    </div>
    <canvas ref="canvas" class="bar-canvas" />
  </div>
</template>

<style scoped>
.bar-wrap {
  width: 100%;
  height: 220px;
  position: relative;
  display: flex;
  flex-direction: column;
}
.bar-canvas {
  width: 100%;
  flex: 1;
  min-height: 0;
  display: block;
}
.bar-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding-bottom: 0.375rem;
  flex-shrink: 0;
}
.bar-nav-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  min-width: 8rem;
  text-align: center;
}
.bar-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.625rem;
  cursor: pointer;
  transition: all 0.15s;
}
.bar-nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.08);
  color: #0b1320;
}
.bar-nav-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

[data-theme='dark'] .bar-nav-label {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .bar-nav-btn {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .bar-nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
</style>
